import React, { useRef, useState, useEffect } from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import WaitlistCTA from "./WaitlistCTA.js";

const rows = [
  {
    dimension: "Materials personalized to your class",
    teacher: { kind: "partial", text: "Deep, but limited to prep time" },
    ai: { kind: "miss", text: "Generic, no context" },
    maestro: { kind: "hit", text: "Personalized + at scale" },
  },
  {
    dimension: "Differentiate for 3+ levels in one room",
    teacher: { kind: "miss", text: "Impossibly time-consuming" },
    ai: { kind: "partial", text: "Fast, but no ownership of the standard" },
    maestro: { kind: "hit", text: "3 tiers, standard-aligned" },
  },
  {
    dimension: "Adapt to last week's data",
    teacher: {
      kind: "partial",
      text: "Reads data, but limited follow-through",
    },
    ai: { kind: "miss", text: "No feedback loop" },
    maestro: { kind: "hit", text: "Weekly coach + skill iteration" },
  },
  {
    dimension: "Preserve teacher judgment",
    teacher: { kind: "hit", text: "Yes, but exhaustively" },
    ai: { kind: "miss", text: "Replaces the teacher's decisions" },
    maestro: { kind: "hit", text: "AI executes; teacher decides" },
  },
  {
    dimension: "Improve every week",
    teacher: { kind: "partial", text: "If they have the bandwidth" },
    ai: { kind: "miss", text: "Static; same prompt as day one" },
    maestro: { kind: "hit", text: "Coach + data update the skill" },
  },
];

const iconMap = {
  hit: { icon: "✓", bg: "#dcfce7", color: "#14532d" },
  partial: { icon: "~", bg: "#fef3c7", color: "#b45309" },
  miss: { icon: "✕", bg: "#fef2f2", color: "#dc2626" },
};

const Cell = ({ signal, isMaestro }) => {
  const theme = useTheme();
  const style = iconMap[signal.kind];
  return (
    <Box
      sx={{
        px: 2.5,
        py: 2.75,
        fontSize: "0.90625rem",
        borderLeft: `1px solid ${theme.palette.text.tertiary}`,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        bgcolor: isMaestro ? "rgba(32, 156, 51, 0.04)" : "transparent",
        color: theme.palette.text.primary,
        fontWeight: isMaestro ? 600 : 400,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
          bgcolor: style.bg,
          color: style.color,
        }}
      >
        {style.icon}
      </Box>
      {signal.text}
    </Box>
  );
};

const WhatsDifferent = () => {
  const theme = useTheme();
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * Track which card is closest to the horizontal center of the scroll
   * container so the dot pagination reflects it. Rounds by dividing scroll
   * offset by card+gap width; keeps in sync during momentum scrolling.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardWidth = el.firstChild?.offsetWidth || 1;
      const gap = 16;
      const idx = Math.round(el.scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(Math.max(idx, 0), rows.length - 1));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToCard = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.offsetWidth || 0;
    const gap = 16;
    el.scrollTo({ left: (cardWidth + gap) * idx, behavior: "smooth" });
  };

  return (
    <Box
      component="section"
      id="different"
      sx={{ py: { xs: 8, md: 14 }, bgcolor: theme.palette.paper.primary }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          What's different
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.text.primary,
          }}
        >
          Human + AI{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            works
          </Box>
          .
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: theme.palette.text.secondary,
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Not a slogan. A working design choice. Every part of Maestro is built
          so that AI and a human coach are doing what neither does well alone.
        </Typography>

        {/* Comparison table - desktop (md+): 4-column grid; mobile (xs-sm): stacked cards per dimension */}
        <Box
          sx={{
            mt: 8,
            bgcolor: theme.palette.paper.primary,
            border: `1px solid ${theme.palette.text.tertiary}`,
            borderRadius: 3,
            overflow: "hidden",
            display: { xs: "none", md: "block" },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.3fr 1fr 1fr 1fr",
              bgcolor: "#fafafa",
              borderBottom: `1px solid ${theme.palette.text.tertiary}`,
            }}
          >
            <Box sx={{ px: 2.5, py: 3 }}>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                }}
              >
                Try to do this at scale
              </Typography>
            </Box>
            {["Teacher alone", "AI alone"].map((label) => (
              <Box
                key={label}
                sx={{
                  px: 2.5,
                  py: 3,
                  textAlign: "center",
                  borderLeft: `1px solid ${theme.palette.text.tertiary}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                px: 2.5,
                py: 3,
                textAlign: "center",
                borderLeft: `1px solid ${theme.palette.text.tertiary}`,
                bgcolor: theme.palette.text.primary,
                color: theme.palette.paper.primary,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  right: 0,
                  height: "3px",
                  bgcolor: theme.palette.success.main,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: theme.palette.paper.primary,
                }}
              >
                <Box
                  component="span"
                  sx={{ color: theme.palette.success.main, mr: 0.5 }}
                >
                  ★
                </Box>
                Maestro (Human + AI)
              </Typography>
            </Box>
          </Box>

          {/* Rows */}
          {rows.map((row, i) => (
            <Box
              key={row.dimension}
              sx={{
                display: "grid",
                gridTemplateColumns: "1.3fr 1fr 1fr 1fr",
                borderBottom:
                  i === rows.length - 1
                    ? "none"
                    : `1px solid ${theme.palette.text.tertiary}`,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2.75,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: "0.96875rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {row.dimension}
              </Box>
              <Cell signal={row.teacher} />
              <Cell signal={row.ai} />
              <Cell signal={row.maestro} isMaestro />
            </Box>
          ))}
        </Box>

        {/* Mobile horizontal snap-scroll carousel - swipe between dimensions, dots below track position */}
        <Box sx={{ display: { xs: "block", md: "none" }, mt: 6 }}>
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              mx: -3,
              px: 3,
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {rows.map((row) => (
              <Box
                key={row.dimension}
                sx={{
                  flex: "0 0 88%",
                  scrollSnapAlign: "center",
                  bgcolor: theme.palette.paper.primary,
                  border: `1px solid ${theme.palette.text.tertiary}`,
                  borderRadius: 2.5,
                  overflow: "hidden",
                }}
              >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: "#fafafa",
                  borderBottom: `1px solid ${theme.palette.text.tertiary}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    lineHeight: 1.35,
                  }}
                >
                  {row.dimension}
                </Typography>
              </Box>
              {[
                { label: "Teacher alone", signal: row.teacher },
                { label: "AI alone", signal: row.ai },
                { label: "Maestro (Human + AI)", signal: row.maestro, isMaestro: true },
              ].map((option, idx, arr) => {
                const style = iconMap[option.signal.kind];
                return (
                  <Box
                    key={option.label}
                    sx={{
                      px: 2.5,
                      py: 2,
                      borderBottom:
                        idx === arr.length - 1
                          ? "none"
                          : `1px solid ${theme.palette.text.tertiary}`,
                      bgcolor: option.isMaestro
                        ? "rgba(32, 156, 51, 0.06)"
                        : "transparent",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                        bgcolor: style.bg,
                        color: style.color,
                        mt: 0.25,
                      }}
                    >
                      {style.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: option.isMaestro
                            ? theme.palette.success.main
                            : theme.palette.text.secondary,
                          mb: 0.5,
                        }}
                      >
                        {option.isMaestro && (
                          <Box component="span" sx={{ mr: 0.5 }}>
                            ★
                          </Box>
                        )}
                        {option.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.9375rem",
                          color: theme.palette.text.primary,
                          fontWeight: option.isMaestro ? 600 : 400,
                          lineHeight: 1.45,
                        }}
                      >
                        {option.signal.text}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          </Box>

          {/* Dot pagination — reflects active card center */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 2.5,
            }}
          >
            {rows.map((row, i) => (
              <Box
                key={row.dimension}
                component="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => scrollToCard(i)}
                sx={{
                  width: activeIndex === i ? 24 : 8,
                  height: 8,
                  borderRadius: 999,
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  bgcolor:
                    activeIndex === i
                      ? theme.palette.success.main
                      : theme.palette.text.tertiary,
                  transition: "width 0.2s, background-color 0.2s",
                }}
              />
            ))}
          </Box>
        </Box>

        <WaitlistCTA />
      </Container>
    </Box>
  );
};

export default WhatsDifferent;
